import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Fixed dd/mm/yyyy display format, independent of the browser/OS locale
// (unlike toLocaleDateString(), which formats differently per system
// settings — e.g. mm/dd/yyyy on an en-US locale). Reads local date
// components (not UTC) to match how new Date(isoString) is already used
// throughout the dashboard for the backend's naive-local datetime strings.
export function formatDate(dateInput) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// The `limit` most recently CREATED subscriptions, newest first.
//
// GET /admin/subscriptions applies no sort, so it hands back Mongo's natural
// (insertion) order — oldest first. Taking the head of that list is what made the
// "Abonamente recente" card show the OLDEST subscriptions.
//
// Sorts a copy, never in place: the same array is also held in component state and
// exported to Excel, and an in-place sort would silently reorder both.
//
// A row whose created_at is missing or unparseable sorts last instead of feeding NaN
// to the comparator — an inconsistent comparator leaves the ENTIRE list in an
// implementation-defined order, so one bad row would scramble the other four.
export function mostRecentSubscriptions(rows, limit) {
  const createdAt = (row) => {
    const time = new Date(row?.subscription?.created_at).getTime();
    return Number.isFinite(time) ? time : -Infinity;
  };
  return [...rows]
    .sort((a, b) => {
      const left = createdAt(a);
      const right = createdAt(b);
      if (left === right) return 0; // also the -Infinity === -Infinity case
      return right > left ? 1 : -1;
    })
    .slice(0, limit);
}

// Combining diacritical marks (U+0300–U+036F). Stripping these after an NFD
// decomposition folds every Romanian diacritic onto its base letter: ă/â/î,
// ș/ț (comma below, U+0326), and the legacy cedilla forms ş/ţ that older
// records still carry. All of them verified.
//
// The explicit range is used rather than \p{Diacritic}: Unicode property
// escapes need the /u flag and depend on how Babel transpiles the regex for
// this project's browserslist targets. No reason to take that dependency for a
// fixed, known set of marks.
const COMBINING_MARKS = /[\u0300-\u036f]/g;

// Lowercase, diacritic-free, and whitespace-normalized, for comparing a search
// box against user data. `?? ''` rather than `|| ''`: nothing these callers
// pass distinguishes the two today, but if a caller ever passes a falsy
// non-nullish value it should still be compared rather than silently dropped.
const foldForSearch = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();

// Users whose name, phone, or email matches `query`.
//
// Diacritic-insensitive in both directions: typing `ionut` finds `Ionuț`, and
// typing `Ionuț` finds a record stored as `Ionut`. On a Romanian user base, a
// name search that misses on diacritics is a search box that gets reported as
// broken.
//
// `"first last"` is matched as a combined string as well as field by field, so
// `ion popescu` finds Ion Popescu — matching only the individual fields would
// return nothing for the most natural thing an operator types.
//
// Purely a text filter: no sorting, and no filtering by role or active status.
// Callers decide who is eligible to appear at all; this decides only who
// matches what was typed. That split is what lets the Users page — which must
// keep showing staff — share it with the subscriptions picker, which must not.
export function filterUsers(users, query) {
  const needle = foldForSearch(query).trim();
  if (!needle) return [...users];

  return users.filter((user) => {
    const first = foldForSearch(user?.first_name);
    const last = foldForSearch(user?.last_name);
    return (
      first.includes(needle) ||
      last.includes(needle) ||
      `${first} ${last}`.includes(needle) ||
      foldForSearch(user?.phone).includes(needle) ||
      foldForSearch(user?.email).includes(needle)
    );
  });
}
